import React, { useState, useEffect, useRef } from "react";
import { useServerApi } from "../hooks/userServerApi";
import _ from "lodash";
import cx from "clsx";
import classes from "./DndListHandle.module.css";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import {
  Timeline,
  Text,
  Avatar,
  Group,
  ActionIcon,
  ScrollArea,
  Box,
  Grid,
  List,
  Button,
  Modal,
  FileInput,
  Anchor,
} from "@mantine/core";
import {
  IconCircleMinus,
  IconTrash,
  IconGripVertical,
  IconPencil,
  IconDownload,
  IconExclamationCircle,
} from "@tabler/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useForceUpdate, useListState } from "@mantine/hooks";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { authName } from "../App";

const OrderList = ({
  form,
  name,
  label,
  scrollHeight = 300,
  onEnterSave = false,
  rowFields = [],
  defaultRowValue = {},
  addLabel = "Add",
  readOnly = false,
  row,
  listHeader: ListHeader,
  showImport = false,
  importTemplateDownloads = [],
  onImport,

  ...props
}) => {
  const [api] = useServerApi();
  const list = _.get(form.values, name) || [];
  const idCounter = useRef(0);
  const [items, setItems] = useState(
    list.map((r, index) => ({
      id: `item-${idCounter.current++}`,
      content: r,
      edit: false,
    }))
  );
  const ref = useRef(null);
  const forceUpdate = useForceUpdate();
  const [openedImport, setOpenedImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Helper function to create items with stable IDs
  const createItemsWithStableIds = (listData, preserveEdit = false) => {
    return listData.map((content, index) => {
      // Try to find existing item with same content to preserve its ID
      const existingItem = items.find((item) => item.content === content);
      return {
        id: existingItem?.id || `item-${idCounter.current++}`,
        content: content,
        edit: preserveEdit && existingItem ? existingItem.edit : false,
      };
    });
  };

  useDeepCompareEffect(() => {
    setItems(createItemsWithStableIds(list, true));
  }, [list]);

  const handleKeyDown = async (e) => {
    if (e.key === "Enter") {
      list.push(e.target.value);
      ref.current.value = "";
      if (onEnterSave) form.updateAndSave({ [name]: list });
      else form.setFieldValue(name, list);
      setItems(createItemsWithStableIds(list));
    }
  };

  const handleAdd = () => {
    list.push({ ...defaultRowValue });
    if (onEnterSave) form.updateAndSave({ [name]: list });
    else form.setFieldValue(name, list);
    const newItems = createItemsWithStableIds(list);
    // Set the newly added item to edit mode
    if (newItems.length > 0) {
      newItems[newItems.length - 1].edit = true;
    }
    setItems(newItems);
  };

  const remove = (index) => {
    // console.log("Remove", list, name, index);
    list.splice(index, 1);
    // form.updateAndSave({ [name]: list });
    // ref.current.value = "";
    if (onEnterSave) form.updateAndSave({ [name]: list });
    else form.setFieldValue(name, list);
    setItems(createItemsWithStableIds(list));
  };

  const edit = (index) => {
    items[index].edit = !items[index].edit;
    forceUpdate();
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    // console.log("reorder", startIndex, endIndex, result);
    result.splice(endIndex, 0, removed);
    // console.log("reorder", startIndex, endIndex, result);
    return [...result];
  };

  // // a little function to help us with reordering the result
  // const reorderBug = (list, startIndex, endIndex) => {
  //   const result = Array.from(list);
  //   const [removed] = result.splice(startIndex, 1);
  //   result.splice(endIndex, 0, removed);

  //   return result;
  // };

  const grid = 8;

  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    // padding: grid * 2,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    // background: isDragging ? "lightgreen" : "grey",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );
    setItems(newItems);
    form.setFieldValue(name, [...newItems.map((i) => i.content)]);
    // forceUpdate();
  };

  const renderViewRow = (item) => {
    return (
      <Group noWrap>
        {/* <JsonViewer src={item} /> */}
        {/* <JsonViewer src={rowFields} /> */}
        {rowFields.map((r) => (
          <r.viewComponent>{item.content[r.name]}</r.viewComponent>
        ))}
      </Group>
    );
  };

  const renderEditRow = (item, index) => {
    return (
      <row.component
        item={item}
        index={index}
        readOnly={readOnly}
        form={form}
        name={name}
      />
    );
  };

  const { t } = useTranslation();

  const resolvedTemplateDownloads = (importTemplateDownloads ?? [])
    .map((item) => ({
      url: item.getUrl ? item.getUrl(form) : item.url,
      text: item.text,
    }))
    .filter((item) => item.url);

  const downloadTemplate = async (url, text) => {
    try {
      const token = localStorage.getItem(authName);
      const result = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "x-access-token": token,
        },
      });
      if (!result.ok) throw new Error("Failed to download template");

      const blob = await result.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      const contentDisposition = result.headers.get("Content-Disposition");
      let fileName = `${text || "template"}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match?.[1]) fileName = match[1];
      }
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showNotification({
        title: "Download failed",
        color: "red",
        icon: <IconExclamationCircle size={18} />,
        message: error?.message ?? String(error),
      });
    }
  };

  const appendImportedRows = (importedRows) => {
    const currentList = _.get(form.values, name) || [];
    const mergedList = [...currentList, ...importedRows];
    if (onEnterSave) form.updateAndSave({ [name]: mergedList });
    else form.setFieldValue(name, mergedList);
    setItems(createItemsWithStableIds(mergedList));
  };

  const handleImport = async () => {
    if (!importFile) return;

    try {
      setImporting(true);
      let importedRows;

      if (onImport) {
        importedRows = await onImport(importFile, form, api);
      } else {
        throw new Error("Import handler is not configured");
      }

      if (!Array.isArray(importedRows)) {
        throw new Error("Import did not return valid rows");
      }

      appendImportedRows(importedRows);
      showNotification({
        title: `${importFile.name} imported successfully`,
        message: `Added ${importedRows.length} row(s)`,
      });
      setOpenedImport(false);
      setImportFile(null);
    } catch (error) {
      showNotification({
        title: `${importFile?.name ?? "File"} import failed`,
        color: "red",
        icon: <IconExclamationCircle size={18} />,
        message: _.isString(error) ? error : error?.message ?? error?.msg,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box {...props}>
      {/* entityId:{entityId} */}
      {/* {<JsonViewer src={_.get(form.values, name)} />} */}
      {label && (
        <Text size={props.size ?? "xs"} mb="xs">
          {label}
        </Text>
      )}
      {/* <TextInput ref={ref} mb={20} onKeyDown={handleKeyDown} /> */}
      {ListHeader && (
        <Grid columns={24} mb={4}>
          {!readOnly && <Grid.Col span={1} />}
          <Grid.Col span={readOnly ? 24 : 22}>
            <ListHeader />
          </Grid.Col>
          {!readOnly && <Grid.Col span={1} />}
        </Grid>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {items.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  isDragDisabled={readOnly}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...(!readOnly ? provided.dragHandleProps : {})}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style
                      )}
                    >
                      <Grid columns={24} align="center">
                        {!readOnly && (
                          <Grid.Col
                            span={1}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconGripVertical size={15}></IconGripVertical>
                          </Grid.Col>
                        )}
                        <Grid.Col span={readOnly ? 24 : 22}>
                          {renderEditRow(item, index)}
                        </Grid.Col>
                        <Grid.Col
                          span={1}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {!readOnly && (
                            <ActionIcon
                              size={"xs"}
                              onClick={() => remove(index)}
                              variant="default"
                            >
                              <IconCircleMinus />
                            </ActionIcon>
                          )}
                        </Grid.Col>
                      </Grid>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {!readOnly && (
        <Group mt="xl" spacing="xs">
          <Button variant="default" size="xs" onClick={() => handleAdd()}>
            {t(addLabel)}
          </Button>
          {showImport && (
            <Button
              variant="default"
              size="xs"
              onClick={() => setOpenedImport(true)}
            >
              {t("Import")}
            </Button>
          )}
        </Group>
      )}

      <Modal
        opened={openedImport}
        onClose={() => {
          if (!importing) {
            setOpenedImport(false);
            setImportFile(null);
          }
        }}
        title={t("Import Data")}
        size="xl"
      >
        <FileInput
          placeholder={t("Pick file")}
          label={t("Data File")}
          withAsterisk
          onChange={setImportFile}
          disabled={importing}
          accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        />
        {resolvedTemplateDownloads.map((item, index) => (
          <Group mt={index === 0 ? "md" : "xs"} spacing="xs" key={item.url}>
            <IconDownload size="1rem" style={{ marginRight: "0.5rem" }} />
            <Anchor
              size="xs"
              component="button"
              type="button"
              onClick={() => downloadTemplate(item.url, item.text)}
            >
              {item.text}
            </Anchor>
          </Group>
        ))}
        <Button onClick={handleImport} mt="xl" size="xs" loading={importing}>
          {t("Import")}
        </Button>
      </Modal>
    </Box>
  );
};

export default OrderList;
